import { useMemo } from "react";
import type { SilaeFormData, SilaeSourceFormData } from "@/types/silae";

/**
 * Collects all user-defined SILAE variable names from the form and returns
 * a Set of names that appear more than once (duplicates).
 *
 * Names that share the same SILAE variable namespace:
 * - stockageVariableName  (bonus storage)
 * - evpForceTitle         (EVP force column)
 * - advancedVariablePostCallVarName (intermediate var after advanced call)
 */
export function useVariableNameCollisions(values: SilaeFormData): Set<string> {
  return useMemo(() => {
    const all: string[] = [];

    const collectSource = (src: SilaeSourceFormData) => {
      if (src.enableEvpForce && src.evpForceTitle?.trim()) {
        all.push(src.evpForceTitle.trim().toUpperCase());
      }
      if (src.advancedVariablePostCallVarName?.trim()) {
        all.push(src.advancedVariablePostCallVarName.trim().toUpperCase());
      }
      if (src.elseSource) collectSource(src.elseSource);
      if (src.complexExpression?.length) {
        src.complexExpression.forEach(collectSource);
      }
    };

    const collectBonuses = (bonuses: SilaeFormData["ifBranch"]["bonuses"]) => {
      bonuses?.forEach((bonus) => {
        if (bonus.enableStockage && bonus.stockageVariableName?.trim()) {
          all.push(bonus.stockageVariableName.trim().toUpperCase());
        }
        bonus.bases?.forEach(collectSource);
        bonus.tauxs?.forEach(collectSource);
      });
    };

    collectBonuses(values.ifBranch?.bonuses ?? []);
    collectBonuses(values.elseBranch?.bonuses ?? []);

    // Find duplicates
    const seen = new Set<string>();
    const duplicates = new Set<string>();
    for (const name of all) {
      if (seen.has(name)) duplicates.add(name);
      else seen.add(name);
    }
    return duplicates;
  }, [values]);
}
